import RequiredMark from '@components/ui/RequiredMark';
import PublicLayout from '@layouts/PublicLayout';
import { auth } from '@services/firebase/config';
import { validatePassword } from '@utils/validators';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { motion } from 'framer-motion';
import { FormEvent, ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [isValidCode, setIsValidCode] = useState(false);
  const [invalidCodeError, setInvalidCodeError] = useState('');
  const [email, setEmail] = useState('');

  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setInvalidCodeError('No se proporcionó un código de restablecimiento');
        return;
      }

      try {
        const emailForReset = await verifyPasswordResetCode(auth, oobCode);
        setIsValidCode(true);
        setEmail(emailForReset);
      } catch (error) {
        console.error('Error al verificar el código:', error);
        setInvalidCodeError(
          'El código de restablecimiento no es válido o ha expirado'
        );
      }
    };

    verifyCode();
  }, [oobCode]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '', submit: '', error: '' }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccess('');
    setErrors({});

    if (!formData.password) {
      setErrors((prev) => ({
        ...prev,
        password: 'La contraseña es requerida',
      }));
      return;
    }
    if (!formData.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: 'La confirmación de contraseña es requerida',
      }));
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setErrors((prev) => ({
        ...prev,
        password: passwordError,
      }));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: 'Las contraseñas no coinciden',
      }));
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode!, formData.password);
      setSuccess('Contraseña restablecida correctamente');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      setErrors({
        submit:
          'Error al restablecer la contraseña. Por favor, intenta nuevamente.',
        error:
          error.message ||
          'Error al restablecer la contraseña. Por favor, intenta nuevamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (invalidCodeError) {
    return (
      <PublicLayout>
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card border border-border shadow rounded-xl"
          >
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <h2 className="text-lg font-medium text-destructive mb-4">
                  Error de validación
                </h2>
                <p className="text-sm text-muted-foreground">
                  {invalidCodeError}
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="mt-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Volver al inicio de sesión
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout title="Restablecer contraseña">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-card border border-border shadow rounded-xl"
        >
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-medium leading-6 text-foreground mb-4">
              Restablecer contraseña
            </h2>
            {email && (
              <p className="text-sm text-muted-foreground mb-6">
                Establece una nueva contraseña para la cuenta: {email}
              </p>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground"
                >
                  Nueva contraseña
                  <RequiredMark />
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    data-testid="input-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder=""
                    className={`block w-full shadow-sm sm:text-sm rounded-md bg-background text-foreground ${
                      errors.password
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-border focus:ring-primary focus:border-primary'
                    }`}
                  />
                  {errors.password && (
                    <p className="mt-2 text-sm text-destructive" role="alert">
                      {errors.password}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-foreground"
                >
                  Confirmar nueva contraseña
                  <RequiredMark />
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    data-testid="input-confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder=""
                    className={`block w-full shadow-sm sm:text-sm rounded-md bg-background text-foreground ${
                      errors.confirmPassword
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-border focus:ring-primary focus:border-primary'
                    }`}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-2 text-sm text-destructive" role="alert">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>

              {(errors.submit || errors.error) && (
                <div className="mt-4 space-y-2" role="alert">
                  {errors.submit && (
                    <div className="text-sm text-destructive" role="alert">
                      {errors.submit}
                    </div>
                  )}
                  {errors.error && (
                    <div className="text-sm text-destructive" role="alert">
                      Detalle del error: {errors.error}
                    </div>
                  )}
                </div>
              )}

              {success && (
                <div
                  className="mt-4 text-sm text-green-600 dark:text-green-400"
                  role="alert"
                >
                  {success}
                </div>
              )}

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isLoading || !isValidCode}
                  className="w-full flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:bg-primary/40"
                >
                  {isLoading ? (
                    <svg
                      className="animate-spin h-5 w-5 text-primary-foreground"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      role="status"
                      aria-label="Cargando"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    'Restablecer contraseña'
                  )}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </PublicLayout>
  );
}
